import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, Loader, AlertCircle, Copy, Download, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseClient } from '../lib/supabase';
import bcrypt from 'bcryptjs';

const DemoLoader = ({ onComplete }) => {
  const [step, setStep] = useState(1); // 1: Schema, 2: Demo Data
  const [loading, setLoading] = useState(false);
  const [schemaCopied, setSchemaCopied] = useState(false);
  const [demoDataLoading, setDemoDataLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Schema SQL'ini yükle
  const [schemaSql, setSchemaSql] = useState('');

  useEffect(() => {
    // public_schema.sql dosyasını yükle
    fetch('/public_schema.sql')
      .then(res => res.text())
      .then(text => setSchemaSql(text))
      .catch(err => {
        console.error('Schema dosyası yüklenemedi:', err);
        toast.error('Schema dosyası yüklenemedi');
      });
  }, []);

  const copySchemaToClipboard = () => {
    if (schemaSql) {
      navigator.clipboard.writeText(schemaSql);
      setSchemaCopied(true);
      toast.success('SQL kopyalandı! Supabase SQL Editor\'e yapıştırın.');
      setTimeout(() => setSchemaCopied(false), 3000);
    }
  };

  const downloadSchema = () => {
    if (schemaSql) {
      const blob = new Blob([schemaSql], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'public_schema.sql';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Schema dosyası indirildi!');
    }
  };

  const checkSchemaExecuted = async () => {
    setLoading(true);
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        toast.error('Supabase bağlantısı bulunamadı');
        return;
      }

      // Tabloların var olup olmadığını kontrol et
      const { error } = await supabase
        .from('users')
        .select('id')
        .limit(1);

      if (error && (error.code === 'PGRST116' || error.message.includes('does not exist'))) {
        toast.error('Schema henüz çalıştırılmamış. Lütfen SQL\'i Supabase SQL Editor\'de çalıştırın.');
      } else {
        setStep(2);
        toast.success('Schema başarıyla oluşturuldu!');
      }
    } catch (error) {
      console.error('Schema kontrol hatası:', error);
      toast.error('Schema kontrol edilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadDemoData = async () => {
    setDemoDataLoading(true);
    setProgress(0);

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        toast.error('Supabase bağlantısı bulunamadı');
        return;
      }

      // Demo verileri yükle
      const demoData = generateDemoData();
      let currentProgress = 0;
      const totalSteps = 16; // Toplam adım sayısı

      // 1. Settings (önce settings yüklenmeli)
      if (demoData.settings && demoData.settings.length > 0) {
        const { error } = await supabase
          .from('settings')
          .insert(demoData.settings);
        if (error) {
          console.error('Settings insert error:', error);
        }
        currentProgress += 1;
        setProgress((currentProgress / totalSteps) * 100);
      }

      // 2. Companies
      if (demoData.companies && demoData.companies.length > 0) {
        const { error } = await supabase
          .from('companies')
          .insert(demoData.companies);
        if (error) {
          console.error('Companies insert error:', error);
          toast.error('Şirketler yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 3. Users (password'leri hash'le)
      if (demoData.users && demoData.users.length > 0) {
        // Password'leri hash'le
        const usersWithHashedPasswords = await Promise.all(
          demoData.users.map(async (user) => {
            const hashedUser = { ...user };
            if (hashedUser.password) {
              const saltRounds = 10;
              hashedUser.password = await bcrypt.hash(hashedUser.password.trim(), saltRounds);
            }
            return hashedUser;
          })
        );

        const { error } = await supabase
          .from('users')
          .insert(usersWithHashedPasswords);
        if (error) {
          console.error('Users insert error:', error);
          toast.error('Kullanıcılar yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 4. Registrars
      if (demoData.registrars && demoData.registrars.length > 0) {
        const { error } = await supabase
          .from('registrars')
          .insert(demoData.registrars);
        if (error) {
          console.error('Registrars insert error:', error);
          toast.error('Domain kayıt şirketleri yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 5. Servers
      if (demoData.servers && demoData.servers.length > 0) {
        const { error } = await supabase
          .from('servers')
          .insert(demoData.servers);
        if (error) {
          console.error('Servers insert error:', error);
          toast.error('Sunucular yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 6. Domain Prices
      if (demoData.domain_prices && demoData.domain_prices.length > 0) {
        const { error } = await supabase
          .from('domain_prices')
          .insert(demoData.domain_prices);
        if (error) {
          console.error('Domain prices insert error:', error);
          toast.error('Domain fiyatları yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 7. Hosting Prices
      if (demoData.hosting_prices && demoData.hosting_prices.length > 0) {
        const { error } = await supabase
          .from('hosting_prices')
          .insert(demoData.hosting_prices);
        if (error) {
          console.error('Hosting prices insert error:', error);
          toast.error('Hosting fiyatları yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 8. Accounts
      if (demoData.accounts && demoData.accounts.length > 0) {
        const { error } = await supabase
          .from('accounts')
          .insert(demoData.accounts);
        if (error) {
          console.error('Accounts insert error:', error);
          toast.error('Hesaplar yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 9. Customers (companies'den ID'ler gerekli)
      const { data: companies } = await supabase
        .from('companies')
        .select('id');
      
      if (demoData.customers && demoData.customers.length > 0 && companies && companies.length > 0) {
        const customersWithCompany = demoData.customers.map((customer, index) => ({
          ...customer,
          company_id: companies[index % companies.length].id
        }));

        const { error } = await supabase
          .from('customers')
          .insert(customersWithCompany);
        if (error) {
          console.error('Customers insert error:', error);
          toast.error('Müşteriler yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 10. Customer Companies (ilişki tablosu)
      const { data: customers } = await supabase
        .from('customers')
        .select('id');
      
      if (companies && companies.length > 0 && customers && customers.length > 0) {
        const customerCompanies = [];
        customers.forEach((customer, index) => {
          customerCompanies.push({
            customer_id: customer.id,
            company_id: companies[index % companies.length].id
          });
        });

        if (customerCompanies.length > 0) {
          const { error } = await supabase
            .from('customer_companies')
            .insert(customerCompanies);
          if (error) {
            console.error('Customer companies insert error:', error);
          } else {
            currentProgress += 1;
            setProgress((currentProgress / totalSteps) * 100);
          }
        }
      }

      // 11. Domains
      const { data: registrars } = await supabase
        .from('registrars')
        .select('id');
      const { data: servers } = await supabase
        .from('servers')
        .select('id');

      if (demoData.domains && demoData.domains.length > 0 && companies && companies.length > 0 && customers && customers.length > 0) {
        const domainsWithRelations = demoData.domains.map((domain, index) => ({
          ...domain,
          company_id: companies[index % companies.length].id,
          customer_id: customers[index % customers.length].id,
          registrar_id: registrars && registrars.length > 0 ? registrars[index % registrars.length].id : null,
          server_id: servers && servers.length > 0 ? servers[index % servers.length].id : null
        }));

        const { error } = await supabase
          .from('domains')
          .insert(domainsWithRelations);
        if (error) {
          console.error('Domains insert error:', error);
          toast.error('Domainler yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 12. Company Payments
      const { data: domains } = await supabase
        .from('domains')
        .select('id');

      if (demoData.company_payments && demoData.company_payments.length > 0 && companies && companies.length > 0) {
        const paymentsWithRelations = demoData.company_payments.map((payment, index) => ({
          ...payment,
          company_id: companies[index % companies.length].id,
          domain_id: domains && domains.length > 0 ? domains[index % domains.length].id : null
        }));

        const { error } = await supabase
          .from('company_payments')
          .insert(paymentsWithRelations);
        if (error) {
          console.error('Company payments insert error:', error);
          toast.error('Tekrarlı ödemeler yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 13. Payments
      if (demoData.payments && demoData.payments.length > 0 && customers && customers.length > 0) {
        const paymentsWithRelations = demoData.payments.map((payment, index) => ({
          ...payment,
          customer_id: customers[index % customers.length].id,
          domain_id: domains && domains.length > 0 ? domains[index % domains.length].id : null,
          server_id: servers && servers.length > 0 ? servers[index % servers.length].id : null
        }));

        const { error } = await supabase
          .from('payments')
          .insert(paymentsWithRelations);
        if (error) {
          console.error('Payments insert error:', error);
          toast.error('Ödemeler yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 14. Receivables
      if (demoData.receivables && demoData.receivables.length > 0 && customers && customers.length > 0) {
        const receivablesWithRelations = demoData.receivables.map((receivable, index) => ({
          ...receivable,
          customer_id: customers[index % customers.length].id
        }));

        const { error } = await supabase
          .from('receivables')
          .insert(receivablesWithRelations);
        if (error) {
          console.error('Receivables insert error:', error);
          toast.error('Alacaklar yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 15. Account Transactions
      const { data: accounts } = await supabase
        .from('accounts')
        .select('id');

      if (demoData.account_transactions && demoData.account_transactions.length > 0 && accounts && accounts.length > 0) {
        const transactionsWithRelations = demoData.account_transactions.map((transaction, index) => ({
          ...transaction,
          account_id: accounts[index % accounts.length].id
        }));

        const { error } = await supabase
          .from('account_transactions')
          .insert(transactionsWithRelations);
        if (error) {
          console.error('Account transactions insert error:', error);
          toast.error('Hesap işlemleri yüklenirken hata oluştu');
        } else {
          currentProgress += 1;
          setProgress((currentProgress / totalSteps) * 100);
        }
      }

      // 16. Server Uptime Logs
      if (servers && servers.length > 0) {
        const uptimeLogs = [];
        servers.forEach((server) => {
          // Her sunucu için son 7 günün loglarını oluştur
          for (let i = 6; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            uptimeLogs.push({
              server_id: server.id,
              is_online: Math.random() > 0.1, // %90 uptime
              response_time: Math.floor(Math.random() * 200) + 50,
              checked_at: date.toISOString()
            });
          }
        });

        if (uptimeLogs.length > 0) {
          const { error } = await supabase
            .from('server_uptime_logs')
            .insert(uptimeLogs);
          if (error) {
            console.error('Server uptime logs insert error:', error);
          }
        }
      }

      toast.success('Demo veriler başarıyla yüklendi!');
      setTimeout(() => {
        if (onComplete) {
          onComplete();
        }
      }, 1000);
    } catch (error) {
      console.error('Demo data load error:', error);
      toast.error('Demo veriler yüklenirken hata oluştu');
    } finally {
      setDemoDataLoading(false);
    }
  };

  const generateDemoData = () => {
    const today = new Date();
    const getDate = (daysOffset) => {
      const date = new Date(today);
      date.setDate(date.getDate() + daysOffset);
      return date.toISOString().split('T')[0];
    };

    return {
      settings: [
        { setting_key: 'exchange_rate_usd', setting_value: '34.50', description: 'USD/TL Döviz Kuru' },
        { setting_key: 'exchange_rate_eur', setting_value: '37.20', description: 'EUR/TL Döviz Kuru' },
        { setting_key: 'exchange_rate_gbp', setting_value: '43.80', description: 'GBP/TL Döviz Kuru' },
        { setting_key: 'exchange_rate_gold', setting_value: '2850.00', description: 'Altın Fiyatı (TL/gr)' },
        { setting_key: 'company_name', setting_value: 'Domain Control', description: 'Şirket Adı' },
        { setting_key: 'company_email', setting_value: 'info@domaincontrol.com', description: 'Şirket E-posta' }
      ],
      companies: [
        {
          name: 'Teknoloji Çözümleri A.Ş.',
          address: 'Maslak Mahallesi, Büyükdere Caddesi No:123, Sarıyer/İstanbul',
          phone: '+90 212 555 0101',
          email: 'info@teknolojicozumleri.com',
          website: 'https://teknolojicozumleri.com',
          tax_number: '1234567890',
          description: 'Yazılım ve teknoloji çözümleri sağlayıcısı',
          latitude: 41.1082,
          longitude: 29.0084,
          country: 'TR',
          state: 'İstanbul',
          is_active: true
        },
        {
          name: 'Dijital Pazarlama Ltd.',
          address: 'Kızılay Mahallesi, Atatürk Bulvarı No:45, Çankaya/Ankara',
          phone: '+90 312 555 0202',
          email: 'info@dijitalpazarlama.com',
          website: 'https://dijitalpazarlama.com',
          tax_number: '2345678901',
          description: 'Dijital pazarlama ve SEO hizmetleri',
          latitude: 39.9208,
          longitude: 32.8541,
          country: 'TR',
          state: 'Ankara',
          is_active: true
        },
        {
          name: 'E-Ticaret Platformu A.Ş.',
          address: 'Konak Mahallesi, Cumhuriyet Bulvarı No:78, Konak/İzmir',
          phone: '+90 232 555 0303',
          email: 'info@eticaretplatformu.com',
          website: 'https://eticaretplatformu.com',
          tax_number: '3456789012',
          description: 'Online e-ticaret platformu ve danışmanlık',
          latitude: 38.4237,
          longitude: 27.1428,
          country: 'TR',
          state: 'İzmir',
          is_active: true
        },
        {
          name: 'Web Tasarım Stüdyosu',
          address: 'Alsancak Mahallesi, Kordon Boyu No:12, Konak/İzmir',
          phone: '+90 232 555 0404',
          email: 'info@webtasarim.com',
          website: 'https://webtasarim.com',
          tax_number: '4567890123',
          description: 'Profesyonel web tasarım ve geliştirme',
          latitude: 38.4337,
          longitude: 27.1528,
          country: 'TR',
          state: 'İzmir',
          is_active: true
        },
        {
          name: 'Bulut Bilişim Hizmetleri',
          address: 'Kadıköy Mahallesi, Bağdat Caddesi No:234, Kadıköy/İstanbul',
          phone: '+90 216 555 0505',
          email: 'info@bulutbilisim.com',
          website: 'https://bulutbilisim.com',
          tax_number: '5678901234',
          description: 'Bulut altyapı ve hosting hizmetleri',
          latitude: 40.9819,
          longitude: 29.0256,
          country: 'TR',
          state: 'İstanbul',
          is_active: true
        }
      ],
      customers: [
        {
          name: 'Ahmet Yılmaz',
          email: 'ahmet.yilmaz@example.com',
          phone: '+90 555 111 22 33',
          address: 'Çankaya, Ankara',
          notes: 'Uzun süreli müşteri, ödeme geçmişi mükemmel',
          is_active: true
        },
        {
          name: 'Ayşe Demir',
          email: 'ayse.demir@example.com',
          phone: '+90 555 222 33 44',
          address: 'Kadıköy, İstanbul',
          notes: 'E-ticaret sitesi sahibi',
          is_active: true
        },
        {
          name: 'Mehmet Kaya',
          email: 'mehmet.kaya@example.com',
          phone: '+90 555 333 44 55',
          address: 'Bornova, İzmir',
          notes: 'Kurumsal web sitesi',
          is_active: true
        },
        {
          name: 'Fatma Şahin',
          email: 'fatma.sahin@example.com',
          phone: '+90 555 444 55 66',
          address: 'Beşiktaş, İstanbul',
          notes: 'Blog ve içerik sitesi',
          is_active: true
        },
        {
          name: 'Ali Öztürk',
          email: 'ali.ozturk@example.com',
          phone: '+90 555 555 66 77',
          address: 'Yenimahalle, Ankara',
          notes: 'Kurumsal email hosting',
          is_active: true
        },
        {
          name: 'Zeynep Arslan',
          email: 'zeynep.arslan@example.com',
          phone: '+90 555 666 77 88',
          address: 'Karşıyaka, İzmir',
          notes: 'Eğitim platformu',
          is_active: true
        },
        {
          name: 'Can Yücel',
          email: 'can.yucel@example.com',
          phone: '+90 555 777 88 99',
          address: 'Şişli, İstanbul',
          notes: 'Portfolio sitesi',
          is_active: true
        },
        {
          name: 'Elif Çelik',
          email: 'elif.celik@example.com',
          phone: '+90 555 888 99 00',
          address: 'Çankaya, Ankara',
          notes: 'Kurumsal danışmanlık',
          is_active: true
        },
        {
          name: 'Burak Doğan',
          email: 'burak.dogan@example.com',
          phone: '+90 555 999 00 11',
          address: 'Konak, İzmir',
          notes: 'Online mağaza',
          is_active: true
        },
        {
          name: 'Selin Aydın',
          email: 'selin.aydin@example.com',
          phone: '+90 555 000 11 22',
          address: 'Üsküdar, İstanbul',
          notes: 'Kişisel blog',
          is_active: true
        }
      ],
      users: [
        {
          username: 'admin',
          email: 'admin@domaincontrol.com',
          password: 'admin123',
          full_name: 'Sistem Yöneticisi',
          role: 'admin',
          is_active: true
        },
        {
          username: 'manager',
          email: 'manager@domaincontrol.com',
          password: 'manager123',
          full_name: 'Yönetici Kullanıcı',
          role: 'manager',
          is_active: true
        },
        {
          username: 'user1',
          email: 'user1@domaincontrol.com',
          password: 'user123',
          full_name: 'Kullanıcı Bir',
          role: 'user',
          is_active: true
        },
        {
          username: 'user2',
          email: 'user2@domaincontrol.com',
          password: 'user123',
          full_name: 'Kullanıcı İki',
          role: 'user',
          is_active: true
        }
      ],
      registrars: [
        {
          name: 'GoDaddy',
          website: 'https://godaddy.com',
          is_disabled: false
        },
        {
          name: 'Namecheap',
          website: 'https://namecheap.com',
          is_disabled: false
        },
        {
          name: 'Cloudflare Registrar',
          website: 'https://cloudflare.com',
          is_disabled: false
        },
        {
          name: 'Google Domains',
          website: 'https://domains.google',
          is_disabled: false
        },
        {
          name: 'Name.com',
          website: 'https://name.com',
          is_disabled: false
        },
        {
          name: 'Hover',
          website: 'https://hover.com',
          is_disabled: false
        }
      ],
      servers: [
        {
          name: 'Production Server 1',
          ip_address: '192.168.1.100',
          hostname: 'prod1.example.com',
          provider: 'DigitalOcean',
          plan: 'Basic Droplet',
          cost: 12.00,
          cost_type: 'monthly',
          currency: 'USD',
          status: 'active',
          uptime_enabled: true,
          expiry_date: getDate(365)
        },
        {
          name: 'Production Server 2',
          ip_address: '192.168.1.101',
          hostname: 'prod2.example.com',
          provider: 'AWS',
          plan: 't3.medium',
          cost: 45.00,
          cost_type: 'monthly',
          currency: 'USD',
          status: 'active',
          uptime_enabled: true,
          expiry_date: getDate(365)
        },
        {
          name: 'Staging Server',
          ip_address: '192.168.1.102',
          hostname: 'staging.example.com',
          provider: 'Linode',
          plan: 'Nanode 1GB',
          cost: 5.00,
          cost_type: 'monthly',
          currency: 'USD',
          status: 'active',
          uptime_enabled: true,
          expiry_date: getDate(180)
        },
        {
          name: 'Development Server',
          ip_address: '192.168.1.103',
          hostname: 'dev.example.com',
          provider: 'Vultr',
          plan: 'Regular Performance',
          cost: 6.00,
          cost_type: 'monthly',
          currency: 'USD',
          status: 'active',
          uptime_enabled: false,
          expiry_date: getDate(90)
        },
        {
          name: 'Backup Server',
          ip_address: '192.168.1.104',
          hostname: 'backup.example.com',
          provider: 'Hetzner',
          plan: 'CX11',
          cost: 4.00,
          cost_type: 'monthly',
          currency: 'EUR',
          status: 'active',
          uptime_enabled: true,
          expiry_date: getDate(365)
        }
      ],
      domain_prices: [
        { extension: '.com', purchase_price: 10.99, sale_price: 15.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.net', purchase_price: 12.99, sale_price: 18.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.org', purchase_price: 11.99, sale_price: 17.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.info', purchase_price: 13.99, sale_price: 19.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.biz', purchase_price: 14.99, sale_price: 20.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.co', purchase_price: 25.99, sale_price: 35.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.io', purchase_price: 40.99, sale_price: 55.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.xyz', purchase_price: 1.99, sale_price: 5.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.online', purchase_price: 30.99, sale_price: 45.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.store', purchase_price: 50.99, sale_price: 70.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.tech', purchase_price: 45.99, sale_price: 65.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { extension: '.app', purchase_price: 15.99, sale_price: 25.99, purchase_currency: 'USD', sale_currency: 'USD', is_active: true }
      ],
      hosting_prices: [
        { hosting_name: 'Basic Hosting', purchase_price: 5.00, sale_price: 10.00, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { hosting_name: 'Premium Hosting', purchase_price: 10.00, sale_price: 20.00, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { hosting_name: 'Business Hosting', purchase_price: 20.00, sale_price: 40.00, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { hosting_name: 'Enterprise Hosting', purchase_price: 50.00, sale_price: 100.00, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { hosting_name: 'WordPress Hosting', purchase_price: 8.00, sale_price: 15.00, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { hosting_name: 'VPS Hosting', purchase_price: 25.00, sale_price: 50.00, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { hosting_name: 'Dedicated Server', purchase_price: 100.00, sale_price: 200.00, purchase_currency: 'USD', sale_currency: 'USD', is_active: true },
        { hosting_name: 'Cloud Hosting', purchase_price: 15.00, sale_price: 30.00, purchase_currency: 'USD', sale_currency: 'USD', is_active: true }
      ],
      accounts: [
        { name: 'Ana Banka Hesabı', currency: 'TRY', balance: 150000.00, is_active: true },
        { name: 'Döviz Hesabı (USD)', currency: 'USD', balance: 5000.00, is_active: true },
        { name: 'Döviz Hesabı (EUR)', currency: 'EUR', balance: 3000.00, is_active: true },
        { name: 'Yedek Nakit', currency: 'TRY', balance: 25000.00, is_active: true },
        { name: 'Kredi Kartı', currency: 'TRY', balance: -5000.00, is_active: true }
      ],
      domains: [
        { domain_name: 'example.com', registration_date: getDate(-365), expiry_date: getDate(0), renewal_date: getDate(-30), status: 'active', ns1: 'ns1.example.com', ns2: 'ns2.example.com', notes: 'Ana domain', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'demo.net', registration_date: getDate(-180), expiry_date: getDate(185), renewal_date: getDate(155), status: 'active', ns1: 'ns1.demo.net', ns2: 'ns2.demo.net', notes: 'Demo sitesi', website_monitoring_enabled: false, domain_owner: 'customer' },
        { domain_name: 'test.org', registration_date: getDate(-90), expiry_date: getDate(275), renewal_date: getDate(245), status: 'active', ns1: 'ns1.test.org', ns2: 'ns2.test.org', notes: 'Test domaini', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'sample.io', registration_date: getDate(-60), expiry_date: getDate(305), renewal_date: getDate(275), status: 'active', ns1: 'ns1.sample.io', ns2: 'ns2.sample.io', notes: 'Örnek proje', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'mysite.com', registration_date: getDate(-730), expiry_date: getDate(-365), renewal_date: getDate(-395), status: 'expired', ns1: 'ns1.mysite.com', ns2: 'ns2.mysite.com', notes: 'Süresi dolmuş domain', website_monitoring_enabled: false, domain_owner: 'customer' },
        { domain_name: 'webapp.co', registration_date: getDate(-30), expiry_date: getDate(335), renewal_date: getDate(305), status: 'active', ns1: 'ns1.webapp.co', ns2: 'ns2.webapp.co', notes: 'Web uygulaması', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'blog.xyz', registration_date: getDate(-120), expiry_date: getDate(245), renewal_date: getDate(215), status: 'active', ns1: 'ns1.blog.xyz', ns2: 'ns2.blog.xyz', notes: 'Blog sitesi', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'shop.store', registration_date: getDate(-45), expiry_date: getDate(320), renewal_date: getDate(290), status: 'active', ns1: 'ns1.shop.store', ns2: 'ns2.shop.store', notes: 'E-ticaret sitesi', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'api.tech', registration_date: getDate(-200), expiry_date: getDate(165), renewal_date: getDate(135), status: 'active', ns1: 'ns1.api.tech', ns2: 'ns2.api.tech', notes: 'API servisi', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'portal.app', registration_date: getDate(-15), expiry_date: getDate(350), renewal_date: getDate(320), status: 'active', ns1: 'ns1.portal.app', ns2: 'ns2.portal.app', notes: 'Müşteri portalı', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'news.info', registration_date: getDate(-300), expiry_date: getDate(65), renewal_date: getDate(35), status: 'active', ns1: 'ns1.news.info', ns2: 'ns2.news.info', notes: 'Haber sitesi', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'forum.online', registration_date: getDate(-250), expiry_date: getDate(115), renewal_date: getDate(85), status: 'active', ns1: 'ns1.forum.online', ns2: 'ns2.forum.online', notes: 'Forum sitesi', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'docs.site', registration_date: getDate(-100), expiry_date: getDate(265), renewal_date: getDate(235), status: 'active', ns1: 'ns1.docs.site', ns2: 'ns2.docs.site', notes: 'Dokümantasyon', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'support.help', registration_date: getDate(-75), expiry_date: getDate(290), renewal_date: getDate(260), status: 'active', ns1: 'ns1.support.help', ns2: 'ns2.support.help', notes: 'Destek sitesi', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' },
        { domain_name: 'landing.page', registration_date: getDate(-20), expiry_date: getDate(345), renewal_date: getDate(315), status: 'active', ns1: 'ns1.landing.page', ns2: 'ns2.landing.page', notes: 'Landing page', website_monitoring_enabled: true, website_status: 'online', domain_owner: 'customer' }
      ],
      company_payments: [
        { package_name: 'Premium Hosting Paketi', package_type: 'hosting', purchase_price: 10.00, sale_price: 20.00, purchase_currency: 'USD', sale_currency: 'USD', payment_probability: 100, amount: 20.00, currency: 'USD', frequency: 'monthly', start_date: getDate(-90), next_payment_date: getDate(0), last_payment_date: getDate(-30), notes: 'Aylık hosting ödemesi', is_active: true },
        { package_name: 'Domain Yenileme', package_type: 'domain', purchase_price: 12.00, sale_price: 18.00, purchase_currency: 'USD', sale_currency: 'USD', payment_probability: 95, amount: 18.00, currency: 'USD', frequency: 'yearly', start_date: getDate(-365), next_payment_date: getDate(0), last_payment_date: getDate(-365), notes: 'Yıllık domain yenileme', is_active: true },
        { package_name: 'SSL Sertifikası', package_type: 'ssl', purchase_price: 50.00, sale_price: 100.00, purchase_currency: 'USD', sale_currency: 'USD', payment_probability: 100, amount: 100.00, currency: 'USD', frequency: 'yearly', start_date: getDate(-180), next_payment_date: getDate(185), last_payment_date: getDate(-180), notes: 'Yıllık SSL sertifikası', is_active: true },
        { package_name: 'Email Hosting', package_type: 'email', purchase_price: 5.00, sale_price: 10.00, purchase_currency: 'USD', sale_currency: 'USD', payment_probability: 100, amount: 10.00, currency: 'USD', frequency: 'monthly', start_date: getDate(-60), next_payment_date: getDate(0), last_payment_date: getDate(-30), notes: 'Aylık email hosting', is_active: true },
        { package_name: 'Backup Servisi', package_type: 'backup', purchase_price: 8.00, sale_price: 15.00, purchase_currency: 'USD', sale_currency: 'USD', payment_probability: 100, amount: 15.00, currency: 'USD', frequency: 'monthly', start_date: getDate(-120), next_payment_date: getDate(0), last_payment_date: getDate(-30), notes: 'Aylık yedekleme servisi', is_active: true },
        { package_name: 'CDN Servisi', package_type: 'cdn', purchase_price: 15.00, sale_price: 30.00, purchase_currency: 'USD', sale_currency: 'USD', payment_probability: 90, amount: 30.00, currency: 'USD', frequency: 'monthly', start_date: getDate(-45), next_payment_date: getDate(0), last_payment_date: getDate(-30), notes: 'Aylık CDN servisi', is_active: true },
        { package_name: 'VPS Sunucu', package_type: 'vps', purchase_price: 25.00, sale_price: 50.00, purchase_currency: 'USD', sale_currency: 'USD', payment_probability: 100, amount: 50.00, currency: 'USD', frequency: 'monthly', start_date: getDate(-30), next_payment_date: getDate(0), last_payment_date: getDate(-30), notes: 'Aylık VPS sunucu', is_active: true },
        { package_name: 'Domain Transfer', package_type: 'domain', purchase_price: 10.00, sale_price: 15.00, purchase_currency: 'USD', sale_currency: 'USD', payment_probability: 80, amount: 15.00, currency: 'USD', frequency: 'one-time', start_date: getDate(-15), next_payment_date: null, last_payment_date: getDate(-15), notes: 'Tek seferlik domain transfer', is_active: true }
      ],
      payments: [
        { amount: 150.00, currency: 'USD', payment_type: 'domain', payment_method: 'credit_card', due_date: getDate(-10), paid_date: getDate(-8), status: 'paid', invoice_number: 'INV-001', notes: 'Domain yenileme ödemesi' },
        { amount: 200.00, currency: 'USD', payment_type: 'hosting', payment_method: 'bank_transfer', due_date: getDate(-5), paid_date: getDate(-3), status: 'paid', invoice_number: 'INV-002', notes: 'Hosting ödemesi' },
        { amount: 100.00, currency: 'USD', payment_type: 'ssl', payment_method: 'credit_card', due_date: getDate(0), status: 'pending', invoice_number: 'INV-003', notes: 'SSL sertifikası ödemesi' },
        { amount: 50.00, currency: 'USD', payment_type: 'hosting', payment_method: 'paypal', due_date: getDate(5), status: 'pending', invoice_number: 'INV-004', notes: 'Email hosting ödemesi' },
        { amount: 300.00, currency: 'USD', payment_type: 'server', payment_method: 'bank_transfer', due_date: getDate(10), status: 'pending', invoice_number: 'INV-005', notes: 'Sunucu ödemesi' },
        { amount: 75.00, currency: 'USD', payment_type: 'domain', payment_method: 'credit_card', due_date: getDate(15), status: 'pending', invoice_number: 'INV-006', notes: 'Yeni domain kaydı' },
        { amount: 120.00, currency: 'USD', payment_type: 'hosting', payment_method: 'credit_card', due_date: getDate(20), status: 'pending', invoice_number: 'INV-007', notes: 'Premium hosting ödemesi' },
        { amount: 250.00, currency: 'USD', payment_type: 'server', payment_method: 'bank_transfer', due_date: getDate(25), status: 'pending', invoice_number: 'INV-008', notes: 'VPS sunucu ödemesi' }
      ],
      receivables: [
        { amount: 500.00, currency: 'USD', description: 'Hosting ve domain hizmetleri', due_date: getDate(-5), received_date: getDate(-3), status: 'received' },
        { amount: 300.00, currency: 'USD', description: 'SSL sertifikası ve güvenlik', due_date: getDate(0), status: 'pending' },
        { amount: 750.00, currency: 'USD', description: 'Kurumsal hosting paketi', due_date: getDate(10), status: 'pending' },
        { amount: 200.00, currency: 'USD', description: 'Domain transfer ücreti', due_date: getDate(15), status: 'pending' },
        { amount: 400.00, currency: 'USD', description: 'Sunucu kiralama', due_date: getDate(20), status: 'pending' },
        { amount: 150.00, currency: 'USD', description: 'Email hosting servisi', due_date: getDate(25), status: 'pending' }
      ],
      account_transactions: [
        { amount: 5000.00, currency: 'TRY', transaction_type: 'income', transaction_date: getDate(-30), description: 'Müşteri ödemesi' },
        { amount: 2500.00, currency: 'TRY', transaction_type: 'expense', transaction_date: getDate(-25), description: 'Sunucu ödemesi' },
        { amount: 1000.00, currency: 'USD', transaction_type: 'income', transaction_date: getDate(-20), description: 'Hosting geliri' },
        { amount: 500.00, currency: 'USD', transaction_type: 'expense', transaction_date: getDate(-15), description: 'Domain kayıt ücreti' },
        { amount: 3000.00, currency: 'TRY', transaction_type: 'income', transaction_date: getDate(-10), description: 'Proje geliri' },
        { amount: 1500.00, currency: 'TRY', transaction_type: 'expense', transaction_date: getDate(-5), description: 'Ofis kirası' },
        { amount: 2000.00, currency: 'EUR', transaction_type: 'income', transaction_date: getDate(0), description: 'Uluslararası müşteri ödemesi' },
        { amount: 800.00, currency: 'USD', transaction_type: 'expense', transaction_date: getDate(0), description: 'Yazılım lisansı' }
      ]
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <Sparkles className="mx-auto h-20 w-20 text-purple-600 dark:text-purple-400 mb-6" />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Demo Veri Yükleme</h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">Veritabanınızı demo verilerle doldurun</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
          {/* Step 1: Schema */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Veritabanı Şemasını Oluşturun</h2>
                  <p className="text-gray-600 dark:text-gray-300">Önce veritabanı tablolarını oluşturmanız gerekiyor</p>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 p-4 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <p className="font-medium mb-2">Talimatlar:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Supabase Dashboard'a gidin</li>
                      <li>SQL Editor sekmesine tıklayın</li>
                      <li>Aşağıdaki SQL kodunu kopyalayın veya indirin</li>
                      <li>SQL Editor'e yapıştırın ve çalıştırın</li>
                      <li>Başarılı olduğunda "Schema Kontrol Et" butonuna tıklayın</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={copySchemaToClipboard}
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <Copy className="h-5 w-5" />
                  <span>{schemaCopied ? 'Kopyalandı!' : 'SQL\'i Kopyala'}</span>
                </button>
                <button
                  onClick={downloadSchema}
                  className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <Download className="h-5 w-5" />
                  <span>SQL Dosyasını İndir</span>
                </button>
              </div>

              <div className="mt-6">
                <button
                  onClick={checkSchemaExecuted}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>Kontrol Ediliyor...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      <span>Schema Kontrol Et</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Demo Data */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Demo Verileri Yükle</h2>
                  <p className="text-gray-600 dark:text-gray-300">Örnek verilerle veritabanınızı doldurun</p>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-400 p-4 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-green-800 dark:text-green-200">
                    <p className="font-medium mb-2">Yüklenecek Demo Veriler:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>6 Sistem Ayarı</li>
                      <li>5 Şirket</li>
                      <li>10 Müşteri</li>
                      <li>4 Kullanıcı (admin, manager, 2 user)</li>
                      <li>6 Domain Kayıt Şirketi</li>
                      <li>5 Sunucu</li>
                      <li>12 Domain Fiyatı</li>
                      <li>8 Hosting Fiyatı</li>
                      <li>5 Hesap (TRY, USD, EUR)</li>
                      <li>15 Domain</li>
                      <li>8 Tekrarlı Ödeme</li>
                      <li>8 Ödeme Kaydı</li>
                      <li>6 Alacak Kaydı</li>
                      <li>8 Hesap İşlemi</li>
                      <li>Sunucu Uptime Logları (son 7 gün)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Admin Bilgileri */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 p-4 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-900 dark:text-blue-200 mb-2">Demo Kullanıcı Bilgileri:</p>
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Admin Kullanıcı:</span>
                          <span className="text-gray-900 dark:text-white font-mono">admin</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Şifre:</span>
                          <span className="text-gray-900 dark:text-white font-mono">admin123</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-700 dark:text-gray-300">Email:</span>
                          <span className="text-gray-900 dark:text-white">admin@domaincontrol.com</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Diğer Demo Kullanıcılar:</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">manager / </span>
                              <span className="font-mono text-gray-900 dark:text-white">manager123</span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">user1 / </span>
                              <span className="font-mono text-gray-900 dark:text-white">user123</span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">user2 / </span>
                              <span className="font-mono text-gray-900 dark:text-white">user123</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                      💡 Demo veriler yüklendikten sonra bu bilgilerle giriş yapabilirsiniz.
                    </p>
                  </div>
                </div>
              </div>

              {demoDataLoading && (
                <div className="space-y-4">
                  <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div
                      className="bg-green-600 h-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-center text-gray-600 dark:text-gray-300">Yükleniyor... %{Math.round(progress)}</p>
                </div>
              )}

              <div className="mt-6">
                <button
                  onClick={loadDemoData}
                  disabled={demoDataLoading}
                  className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {demoDataLoading ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>Demo Veriler Yükleniyor...</span>
                    </>
                  ) : (
                    <>
                      <Database className="h-5 w-5" />
                      <span>Demo Verileri Yükle</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoLoader;

